import cx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';

import { SearchClearIcon, SearchIcon } from '@/components/icons';
import { SMALL_BREAKPOINT } from '@/components/modular-grid/constants';
import { SearchHeaderTextsTypes, Size } from '@/components/SearchHeader/types';
import { useWindowSize } from '@/hooks/useWindowSize';
import { formatMessage } from '@/utils';
import { formatNumberOfSearchGames } from '@/utils/formatNumberOfSearchGames';

import styles from './SearchInput.module.scss';

interface SearchInputProps {
  state: string;
  isSearchCategoryActive: boolean;
  texts: SearchHeaderTextsTypes;
  size: Size;
  onSearchClose: () => void;
  onChange?: (args: { value: string; sourceType: string }) => void;
  onClearButtonClick?: (args: { value: string }) => void;
  onSearchButtonClick?: (args: { value: string; sourceType: string }) => void;
  onSoftKeyboardVisibilityChange?: (args: {
    behavior: 'open' | 'close';
  }) => void;
  debounceTimeout?: number;
  searchPhrase?: string;
  inputConfig?: {
    isIOS: boolean;
    isRenderedInIFrame: boolean;
  };
  suggestedPhrase?: string;
  numberOfAllSearchGames?: number;
}

const SearchInput: React.FC<SearchInputProps> = ({
  state,
  isSearchCategoryActive,
  size,
  texts,
  onSearchClose,
  onChange,
  onClearButtonClick,
  onSearchButtonClick,
  onSoftKeyboardVisibilityChange,
  debounceTimeout = 350,
  inputConfig = {
    isIOS: false,
    isRenderedInIFrame: false,
  },
  suggestedPhrase,
  numberOfAllSearchGames,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const windowWidth = useWindowSize().width;
  const [phrase, setPhrase] = useState<string>('');
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const [searchInputFocused, setSearchInputFocused] = useState<boolean>(true);
  const formatedNumberOfAllSearchGames = formatNumberOfSearchGames(
    numberOfAllSearchGames
  );
  const placeholderText = formatedNumberOfAllSearchGames
    ? formatMessage(texts.placeholder, {
        numberOfAllSearchGames: formatedNumberOfAllSearchGames,
      })
    : '';

  /**
   * A delay is added to make the cursor animation initiation pleasing to the eye
   * and to ensure that the input is focused correctly after the entire modal is re-rendered.
   */
  useEffect(() => {
    setTimeout(() => {
      inputRef.current?.focus();
    }, 500);
  }, []);

  useEffect(() => {
    if (suggestedPhrase) {
      setShouldRefetch(false);
      setPhrase(suggestedPhrase);
    }
  }, [suggestedPhrase]);

  useEffect(() => {
    if (shouldRefetch && phrase && phrase.length >= 2) {
      const handler = setTimeout(() => {
        onChange && onChange({ value: phrase, sourceType: 'Search Input' });
      }, debounceTimeout);

      return () => {
        clearTimeout(handler);
      };
    }
    return () => {};
  }, [phrase, debounceTimeout, onChange, shouldRefetch]);

  useEffect(() => {
    if (
      inputConfig.isIOS &&
      inputConfig.isRenderedInIFrame &&
      windowWidth <= SMALL_BREAKPOINT.max
    ) {
      onSoftKeyboardVisibilityChange &&
        onSoftKeyboardVisibilityChange({
          behavior: searchInputFocused ? 'open' : 'close',
        });
    }
  }, [
    searchInputFocused,
    inputConfig,
    onSoftKeyboardVisibilityChange,
    windowWidth,
  ]);

  const onFocus = () => setSearchInputFocused(true);
  const onBlur = () => setSearchInputFocused(false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShouldRefetch(true);
    setPhrase(event.target.value);
  };

  const handleClearClick = () => {
    onClearButtonClick && onClearButtonClick({ value: '' });
    setPhrase('');
    inputRef.current?.focus();
  };

  const handleCancelClick = () => {
    onSearchClose && onSearchClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearchButtonClick!({ value: phrase, sourceType: 'Search Input' });
      setPhrase('');
    }
  };

  const clearVisible =
    isSearchCategoryActive ||
    state === 'no_results' ||
    state === 'phrase_too_short';

  return (
    <div
      data-testid="search-wrapper"
      className={cx(
        styles.search,
        isSearchCategoryActive ? styles.searchActive : styles.searchInactive,
        size === 'wide' && styles.wide
      )}
    >
      <div className={cx(styles.inputWrapper, size === 'wide' && styles.wide)}>
        <div data-testid="search-icon" className={styles.searchIconWrapper}>
          <SearchIcon />
        </div>
        <input
          id="search-input"
          className={styles.input}
          readOnly={false}
          placeholder={placeholderText}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={onFocus}
          onBlur={onBlur}
          value={phrase}
          data-testid="search-input"
          maxLength={50}
          ref={inputRef}
        />
        {clearVisible &&
          (size === 'wide' ? (
            <span
              className={styles.clear}
              data-testid="search-clear"
              onClick={handleClearClick}
            >
              {texts.clearText || 'Clear'}
            </span>
          ) : (
            <span
              data-testid="search-clear"
              className={styles.searchClearIconWrapper}
              onClick={handleClearClick}
            >
              <SearchClearIcon />
            </span>
          ))}
      </div>
      {size !== 'wide' && (
        <div className={styles.cancelButtonWrapper}>
          <span
            className={styles.cancelButton}
            data-testid="search-cancel"
            onClick={handleCancelClick}
          >
            {texts.cancelText || 'Cancel'}
          </span>
        </div>
      )}
    </div>
  );
};

export default SearchInput;
